
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, Search, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getCachedRedirect, cacheRedirect, normalizePath, clearRedirectCache } from '@/utils/redirectUtils';
import { Helmet } from "react-helmet";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [suggestedPages, setSuggestedPages] = useState<{url: string, title: string}[]>([]);
  const path = location.pathname;

  useEffect(() => {
    console.log(`[404] Page not found: ${path}`);
    
    // Log 404 errors for analytics
    const logPageNotFound = async () => {
      try {
        await supabase.functions.invoke('analytics', {
          body: { 
            event: 'page_not_found',
            path,
            referrer: document.referrer
          },
        }).catch(err => {
          console.error('[404] Failed to log 404:', err);
        });
      } catch (error) {
        console.error('[404] Error logging 404:', error);
      }
    };
    
    // Try to find suggested pages based on the path
    const findSuggestedPages = async () => {
      // This is a simplified approach; in a real implementation, 
      // you might want to use a more sophisticated algorithm
      try {
        // Blog post special case - suggest the blog index
        if (path.includes('penetration-testing') || path.includes('/blog/')) {
          setSuggestedPages([
            { url: '/blog', title: 'Blog' },
            { url: '/services/managed-cybersecurity', title: 'Managed Cybersecurity Services' },
            { url: '/', title: 'Home' }
          ]);
        } else {
          // Default suggestions
          setSuggestedPages([
            { url: '/', title: 'Home' },
            { url: '/services/managed-cybersecurity', title: 'Managed Cybersecurity Services' },
            { url: '/services/microsoft-365-security', title: 'Microsoft 365 Security' },
            { url: '/blog', title: 'Blog' }
          ]);
        }
      } catch (error) {
        console.error('[404] Error finding suggested pages:', error);
      }
    };
    
    const checkRedirect = async () => {
      try {
        setIsLoading(true);
        
        // Normalize the path for consistency
        const normalizedPath = normalizePath(path);
        
        // First check the cache
        const cachedRedirect = getCachedRedirect(normalizedPath);
        if (cachedRedirect) {
          console.log(`[404] Using cached redirect for ${path} → ${cachedRedirect}`);
          navigate(cachedRedirect, { replace: true });
          return;
        }
        
        console.log('[404] No cached redirect found, checking with unified-redirector');
        
        // Make a direct call to the edge function with better error handling
        const { data, error } = await supabase.functions.invoke('unified-redirector', {
          body: { path: normalizedPath },
        }).catch(err => {
          console.error('[404] Error invoking unified-redirector:', err);
          return { data: null, error: err };
        });
        
        if (error) {
          console.error('[404] Error checking redirects:', error);
          
          // Do not cache errors
          
          toast.error('Failed to check for page redirects', {
            description: 'There was a problem checking if this page has moved.'
          });
          
          findSuggestedPages();
          logPageNotFound();
          return;
        }
        
        // If it's a "No redirect found" response (not an error anymore)
        if (data?.found === false || data?.message === "No redirect found") {
          console.log(`[404] No redirect found for ${path}`);
          cacheRedirect(normalizedPath, null);
          findSuggestedPages();
          logPageNotFound();
          return;
        }
        
        // If a redirect was found
        if (data?.Location) {
          console.log(`[404] Found redirect for ${path} → ${data.Location}`);
          // Cache the result
          cacheRedirect(normalizedPath, data.Location);
          // Redirect to the destination
          navigate(data.Location, { replace: true });
          return;
        }
        
        // If we get an unexpected response format
        console.warn('[404] Unexpected response format:', data);
        
        // Cache negative result for unexpected formats
        cacheRedirect(normalizedPath, null);
        
        // Find suggested pages and log the 404
        findSuggestedPages();
        logPageNotFound();
      } catch (error) {
        console.error('[404] Error checking redirects:', error);
        findSuggestedPages();
        logPageNotFound();
      } finally {
        setIsLoading(false);
        setIsRetrying(false);
      }
    };

    checkRedirect();
  }, [path, navigate, isRetrying]);
  
  const handleRetryRedirect = () => {
    // Clear the cache for this specific path
    const normalizedPath = normalizePath(path);
    cacheRedirect(normalizedPath, undefined); // Passing undefined clears the cache entry
    
    setIsRetrying(true);
    // This will trigger the useEffect again
  };

  if (isLoading || isRetrying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-edgefi-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg">{isRetrying ? "Retrying redirect check..." : "Checking for redirects..."}</p>
          <p className="text-sm text-gray-500 mt-2">One moment please</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Page Not Found | edgefi</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="The page you're looking for could not be found. We suggest checking out our most popular resources or using our search." />
        <link rel="canonical" href="https://edgefi.com/404" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        
        {/* Add structured data for better search engine understanding */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://edgefi.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Page Not Found",
                "item": "https://edgefi.com/404"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Navbar />
      
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50 py-16">
        <div className="text-center max-w-lg px-4">
          <h1 className="text-5xl font-bold text-edgefi-purple mb-6">404</h1>
          <p className="text-2xl font-semibold text-gray-900 mb-3">Page not found</p>
          <p className="text-gray-600 mb-8">
            We couldn't find the page you're looking for. It might have been moved, renamed, or no longer exists.
          </p>
          
          <div className="mb-8 flex flex-wrap items-center justify-center gap-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-edgefi-purple text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to Home
            </Link>
            
            <Button
              variant="outline"
              onClick={handleRetryRedirect}
              className="inline-flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
              Retry Redirect Check
            </Button>
          </div>
          
          {suggestedPages.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-semibold mb-4">You might be looking for:</h2>
              <ul className="space-y-3">
                {suggestedPages.map((page, index) => (
                  <li key={index}>
                    <Link 
                      to={page.url}
                      className="text-edgefi-purple hover:underline inline-flex items-center"
                    >
                      <span>{page.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="mt-12 p-4 bg-white rounded-lg border border-gray-200">
            <h2 className="text-lg font-medium mb-3">Search our site</h2>
            <form 
              className="flex gap-2" 
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const input = form.elements.namedItem('search') as HTMLInputElement;
                window.location.href = `https://edgefi.com/search?q=${encodeURIComponent(input.value)}`;
              }}
            >
              <input 
                type="text"
                name="search"
                placeholder="Search edgefi.com..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-edgefi-purple focus:border-transparent"
              />
              <Button type="submit" className="inline-flex items-center gap-2">
                <Search className="h-4 w-4" />
                Search
              </Button>
            </form>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default NotFound;
